import ENV from 'scaleflow/config/environment';

/**
 * Key construction and entry shaping for manually uploaded repository
 * archives.
 *
 * Two prefixes exist in the VDR bucket and the difference matters:
 *
 * - The **library**, `{tenant}/manual_uploads/source_code/`, is tenant-level
 *   and durable. Archives live here to be reused across scans.
 * - The **scan prefix**, `{tenant}/xrays/{scan}/uploads/repositories/`, is what
 *   the analysis pipeline actually reads, and is the only location a
 *   `repository_url` ever points at.
 *
 * Picking an archive from the library copies it into the scan prefix first, so
 * the backend needs no knowledge of the library at all and a library archive
 * can never be the thing a scan depends on.
 */

/** Leaf under a tenant that holds the reusable archive library. */
export const LIBRARY_SEGMENT = 'manual_uploads/source_code';

export type Repository = {
  repository_url: string;
  name: string;
  full_name: string;
  private: boolean;
  status: string;
  source?: string;
  filename?: string;
};

export function libraryPrefix(tenantId: string): string {
  return `${tenantId}/${LIBRARY_SEGMENT}/`;
}

export function libraryKey(tenantId: string, filename: string): string {
  return `${libraryPrefix(tenantId)}${filename}`;
}

export function scanUploadKey(
  tenantId: string,
  scanId: string,
  filename: string,
): string {
  return `${tenantId}/xrays/${scanId}/uploads/repositories/${filename}`;
}

/**
 * One archive = one repo (v1 scope) — the base name, minus its extension, is
 * the only thing available to key a manual upload by, since there is no
 * owner/repo structure the way a GitHub `full_name` has one. Uploads are
 * restricted to `.tar.gz` (see `Scans::RepositoryUploads`), a compound
 * extension that a single-suffix strip would only half remove.
 */
export function baseName(filename: string): string {
  return filename.replace(/\.tar\.gz$/i, '');
}

/**
 * `bucket` is a parameter rather than an `ENV` read because the credentials
 * endpoint now tells the client which bucket it scoped its credentials to, and
 * that answer can differ from `ENV.storage.AWSS3.vdr_bucket`. If it ever did,
 * reading `ENV` here would upload the bytes to one bucket and hand the backend
 * a `repository_url` pointing at another — with no error anywhere. Callers pass
 * `s3VdrClient.vdrBucket`, so there is one source of truth.
 */
export function buildVdrObjectUrl(bucket: string, key: string): string {
  return `https://${bucket}.s3.${ENV.aws.region}.amazonaws.com/${key}`;
}

/**
 * The single definition of what a manually sourced repository entry looks like.
 *
 * Both ways of adding one — uploading a fresh archive onto a scan, and picking
 * an existing archive out of the library — go through here, so the two cannot
 * drift apart. In particular `repository_url` is always the **scan** key, never
 * the library key.
 *
 * `status` is left to the caller: it is `PENDING` once the bytes are in place
 * and `FAILED` if they never got there, and is never `COMPLETED` from the
 * frontend — the backend owns that transition once it has copied the archive
 * into the workspace.
 */
export function manualRepositoryEntry({
  bucket,
  tenantId,
  scanId,
  filename,
}: {
  bucket: string;
  tenantId: string;
  scanId: string;
  filename: string;
}): Omit<Repository, 'status'> {
  const name = baseName(filename);

  return {
    repository_url: buildVdrObjectUrl(
      bucket,
      scanUploadKey(tenantId, scanId, filename),
    ),
    name,
    full_name: name,
    private: true,
    source: 'manual',
    filename,
  };
}

/**
 * Human-readable byte size. Archives run from a few hundred KB to several GB,
 * so the unit is picked rather than fixed.
 */
export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  // Whole numbers for bytes, one decimal above that — "1.4 MB", not "1.4 B".
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`;
}

/**
 * Relative upload time. `Intl.RelativeTimeFormat` rather than `DateTime.fromNow`: this is
 * the only place the app needs it, and it needs no locale data shipped.
 */
export function formatUploadedAt(
  lastModified: Date | null,
  now: Date = new Date(),
): string {
  if (!lastModified) return '\u2014';

  const seconds = Math.round((lastModified.getTime() - now.getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const divisions: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  let remaining = seconds;
  for (const [size, unit] of divisions) {
    if (Math.abs(remaining) < size) {
      return formatter.format(Math.round(remaining), unit);
    }
    remaining /= size;
  }
  return formatter.format(Math.round(remaining), 'year');
}

/** Per-file upload progress, as the uploader renders it. */
export type UploadProgressEntry = {
  filename: string;
  loaded: number;
  total: number;
};

export function progressPercent({
  loaded,
  total,
}: UploadProgressEntry): number {
  if (!total) return 0;
  // Clamped because lib-storage can report `loaded` slightly past `total` on the
  // final part, which would otherwise render as 101%.
  return Math.min(100, Math.round((loaded / total) * 100));
}

/**
 * Aggregate across a batch. Uploads run in parallel, so a single bar tracking
 * one file would misrepresent the whole; summing bytes does not.
 */
export function aggregateProgress(entries: UploadProgressEntry[]): {
  loaded: number;
  total: number;
  percent: number;
} {
  const loaded = entries.reduce((sum, e) => sum + e.loaded, 0);
  const total = entries.reduce((sum, e) => sum + e.total, 0);
  return {
    loaded,
    total,
    percent: progressPercent({ filename: '', loaded, total }),
  };
}
